import { upgradeToPremium } from "../../../app/api/upgradeToPremium";
import { getTrainClassTypeBaseOnProduct, filterTripsAndOffersWithPrebookStatus } from "../../../Lib/Utils/validationUtil";
import { IBooking, IBookedOffer, IPassenger, IOffer, IExchangeOfferResponse, IUpgradeExchangeOfferResponse, ITrip, ILeg } from "../../../Orchestrator/Types/Index";
import { TripTypeEnum, SHUTTLE_PRODUCT_CODE, ServiceClassTypeEnum, ProductTypeEnum, AdmissionStatusEnum, isShuttleOffer } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { preferTrainTrips } from "../../../Lib/Utils/booking";

// productTag conventions on the /exchange-offers/upgrades response:
// `UPGRADE=<code>` on the source standard offer points at the intended
// premium tier; `RECOMMENDED` marks the user's current source product so
// its UPGRADE= tag wins when multiple sibling offers carry tags.
const UPGRADE_TAG_PREFIX = "UPGRADE=";
const RECOMMENDED_TAG = "RECOMMENDED";

export function getOffersByDirection(bookedOffers: IBookedOffer[] = [], trips: any[] = [], direction: TripTypeEnum): IBookedOffer[] {
    const tripIdByDirection = new Map<string, string>();
    trips.forEach((t: any) => {
        if (t?.id && t?.direction) tripIdByDirection.set(t.id, t.direction);
    });
    return bookedOffers.filter((offer: IBookedOffer) => {
        const coveredTripId = offer?.tripCoverage?.coveredTripId;
        const dir = coveredTripId ? tripIdByDirection.get(coveredTripId) : undefined;
        return dir === direction;
    });
}

// Single source of truth for "what tier is THIS leg currently booked in?".
// Resolves the leg's current booked admission product code (e.g. STV/SFF) so
// the upgrade target can follow its own tier (Value→PRV, Flex→PFF) instead of
// the backend's generic RECOMMENDED tag. All three upgrade entry paths
// (ProcessUpgradeOffersAction, requestUpgradeOffersForBooking, UpgradePremiumTrip)
// call this so they resolve the SAME code for the same booking.
//
// `offers` must already be narrowed to a single direction. An offer is skipped
// only when ALL its admissions are exchanged-out (EXCHANGED) or mid-exchange
// (EXCHANGE_ONGOING), so a partially-exchanged offer still reports the tier its
// remaining passengers hold. This is intentionally STRICTER than
// GetAllOfferWithoutExchangeAction (which excludes only EXCHANGED): a leg whose
// admission is mid-exchange has no settled current tier, so it should fall
// through to RECOMMENDED rather than anchor on a tier that is changing. This
// matches GetProcessBookingInitAction, which also treats both statuses as
// exchanged. Returns undefined when nothing resolves, which makes the caller
// fall back to the prior RECOMMENDED-based behaviour (no regression).
export function resolveLegSourceCode(offers: IBookedOffer[] = []): string | undefined {
    for (const offer of offers) {
        const admissions = offer?.admissions || [];
        const hasActiveAdmission = admissions.length === 0 || admissions.some(
            (a: any) => a?.status !== AdmissionStatusEnum.EXCHANGED && a?.status !== AdmissionStatusEnum.EXCHANGE_ONGOING
        );
        if (!hasActiveAdmission) continue;
        const products = offer?.products || [];
        const admission = products.find((p: any) => p?.type === ProductTypeEnum.ADMISSION_POINT2POINT) || products[0];
        if (admission?.code) return admission.code;
    }
    return undefined;
}

// Which trips/offers an upgrade may target once the prebook narrowing is
// applied.
//
// The narrowing exists for change-date checkout (the upgrade targets the
// PREBOOKED train). An onward shuttle added post-payment is ALSO a PREBOOKED
// offer — honoring the narrowing then scopes upgrades to just the shuttle,
// whose admission is not upgradable, so the request comes back 400
// BOOKING_MODIFICATION_NOT_ALLOWED and the upgrade silently dies. Only narrow
// when a non-shuttle prebooked offer exists.
//
// Shared (rather than inlined at each call site) because a caller that skips
// the gate reintroduces that exact 400 — which is how the outbound-only
// upgrade broke on K5SNQN. Every entry path must scope through here.
export function scopeUpgradeToPrebookedTrain<
    T extends { id: string },
    U extends {
        admissions?: Array<{ status: string }>;
        products?: ({ code?: string | null; type?: string | null } | null)[] | null;
    },
>(trips: T[], offers: U[]): { tripsForUse: T[]; offersForUse: U[] } {
    const { filteredTrips, filteredBookedOffers } = filterTripsAndOffersWithPrebookStatus(trips, offers);
    // isShuttleOffer, NOT `products.some(p => p.code === SHUTTLE_PRODUCT_CODE)`:
    // the constant is the bare "SHTL" but the wire code varies by environment
    // ("SHTL" on int, "SHTL_ADM" in captured preprod bookings), so `===` misses
    // the shuttle and the gate opens on exactly the bookings it exists to catch.
    // It also skips ANCILLARY_ITEM products — a TRAIN offer carrying an in-journey
    // shuttle ancillary ("SHTL-FUJ-BUS") would otherwise read as a shuttle offer
    // and wrongly hold the gate shut. See the note in Lib/Utils/booking.ts.
    const prebookHasTrainOffer = (filteredBookedOffers || []).some((offer) => !isShuttleOffer(offer));
    return {
        tripsForUse: (prebookHasTrainOffer && filteredTrips.length > 0) ? filteredTrips : trips,
        offersForUse: (prebookHasTrainOffer && filteredBookedOffers.length > 0) ? filteredBookedOffers : offers,
    };
}

function getFulfilledFulfillmentIds(offers: IBookedOffer[] = []): string[] {
    const ids = new Set<string>();
    offers.forEach((offer) => {
        (offer?.admissions || [])
            .filter((a: any) => a?.status === "FULFILLED" && a?.id)
            .forEach((a: any) => ids.add(a.id));
    });
    return Array.from(ids);
}

function getPassengerRefsFromOffers(offers: IBookedOffer[] = [], passengers: IPassenger[] = []): string[] {
    if (!offers.length) return [];
    const idToExternalRef = new Map<string, string>();
    passengers.forEach((p) => {
        if (p?.id && p?.externalRef) idToExternalRef.set(p.id, p.externalRef);
    });

    const refs: string[] = [];
    const seen = new Set<string>();
    offers.forEach((offer) => {
        (offer?.admissions || []).forEach((adm: any) => {
            if (adm?.status !== "FULFILLED") return;
            const paxIds: string[] = Array.isArray(adm?.passengerIds) ? adm.passengerIds : [];
            paxIds.forEach((pid) => {
                const ref = idToExternalRef.get(pid);
                if (ref && !seen.has(ref)) {
                    seen.add(ref);
                    refs.push(ref);
                }
            });
        });
    });

    return refs;
}

export function processOffers(trip: ITrip, offers: IOffer[], currentSourceCode?: string): { trip: ITrip; premiumOffers: IOffer[] } | null {
    const now = new Date();
    const tripStartTime = new Date(trip.startTime);

    // Skip trips that have already started today
    if (tripStartTime < now) {
        return null;
    }

    const premiumOffers: IOffer[] = [];
    const shuttleOffers: IOffer[] = [];

    for (const offer of offers) {
        const coveredTripId = offer.tripCoverage?.coveredTripId;
        const coveredLegIds = offer.tripCoverage?.coveredLegIds || [];

        const isMatchingTrip = coveredTripId === trip.id;
        const isMatchingLeg = trip.legs?.some((leg: ILeg) => coveredLegIds.includes(leg.id)) ?? false;

        if (isMatchingTrip && isMatchingLeg) {
            const productCode = offer.products?.[0]?.code;
            const serviceClassType = offer.products?.[0]?.serviceClass?.type;
            if (serviceClassType === ServiceClassTypeEnum.HIGH) {
                premiumOffers.push(offer);
            } else if (productCode === SHUTTLE_PRODUCT_CODE) {
                shuttleOffers.push(offer);
            }
        }
    }

    const legs = trip?.legs || [];
    const trainLegIds = legs.filter((l: ILeg) => l?.timedLeg?.service?.mode?.ptMode !== "BUS").map((l: ILeg) => l.id);
    const shuttleLegIds = legs.filter((l: ILeg) => l?.timedLeg?.service?.mode?.ptMode === "BUS").map((l: ILeg) => l.id);

    const hasAllIds = (offers: IOffer[]) => offers.length > 0 && offers.every(o => !!o?.offerId);
    const coversAllLegs = (offers: IOffer[], requiredLegIds: string[]) => {
        if (!hasAllIds(offers) || requiredLegIds.length === 0) return false;
        const covered = new Set<string>();
        offers.forEach(o => (o?.tripCoverage?.coveredLegIds || []).forEach((id: string) => covered.add(id)));
        return requiredLegIds.every(id => covered.has(id));
    };

    // The exchange-offers/upgrades response returns multiple HIGH-class
    // products at different price points (e.g. PRV/PFF/PRS). Selection is
    // two-tier:
    //   1. If any source offer carries an UPGRADE=<code> productTag (the
    //      backend's hint at the intended premium tier), narrow the pool to
    //      premium offers whose product code matches. A RECOMMENDED tag on
    //      the same offer marks the user's current source product, so its
    //      UPGRADE= tag wins when multiple are present.
    //   2. Among the resulting pool (or all premium offers if no tag matched),
    //      pick the lowest exchangePrice.amount that singly covers all train
    //      legs. Missing exchangePrice sorts last.
    // Tag matching is case-insensitive on both sides (prefix and product code)
    // — the backend has been observed to send mixed-case productTags and the
    // existing checks in GetSearchResultAction / FetchExchangeOffersAction
    // also normalise to uppercase before comparing.
    const findUpgradeTarget = (offs: IOffer[], sourceCode?: string): string | null => {
        const normalizedSource = sourceCode ? sourceCode.toUpperCase() : null;
        let bySource: string | null = null;
        let recommended: string | null = null;
        let first: string | null = null;
        for (const o of offs) {
            for (const p of o?.products || []) {
                const tags = (p as any)?.productTags;
                if (!Array.isArray(tags)) continue;
                const stringTags = tags.filter((t: unknown): t is string => typeof t === "string");
                const isRecommended = stringTags.some(t => t.toUpperCase() === RECOMMENDED_TAG);
                // The source product whose code matches THIS leg's current booked
                // product carries the intended premium tier (Value→PRV, Flex→PFF,
                // Saver→PRS). It wins over RECOMMENDED, which the backend sends
                // generically (same product for every leg) and would otherwise
                // collapse a mixed-class round trip onto a single premium tier.
                const isCurrentSource =
                    normalizedSource != null && (p?.code || "").toUpperCase() === normalizedSource;
                for (const t of stringTags) {
                    if (!t.toUpperCase().startsWith(UPGRADE_TAG_PREFIX)) continue;
                    const code = t.slice(UPGRADE_TAG_PREFIX.length).trim().toUpperCase();
                    if (!code) continue;
                    if (first == null) first = code;
                    if (isRecommended && recommended == null) recommended = code;
                    if (isCurrentSource && bySource == null) bySource = code;
                }
            }
        }
        return bySource ?? recommended ?? first;
    };
    const targetCode = findUpgradeTarget(offers, currentSourceCode);
    const matching = targetCode
        ? premiumOffers.filter(o =>
            Array.isArray(o?.products) && o.products.some(p => (p?.code || "").toUpperCase() === targetCode))
        : [];
    const candidates = matching.length > 0 ? matching : premiumOffers;

    const offerAmount = (o: IOffer) => o?.exchangePrice?.amount ?? Number.POSITIVE_INFINITY;
    const cheapestSingleCovering = (offers: IOffer[], requiredLegIds: string[]): IOffer | null => {
        const covering = offers.filter(o => {
            if (!o?.offerId) return false;
            const ids = o?.tripCoverage?.coveredLegIds || [];
            return requiredLegIds.every(legId => ids.includes(legId));
        });
        if (!covering.length) return null;
        return covering.reduce((min, o) => (offerAmount(o) < offerAmount(min) ? o : min));
    };
    const cheapestOffer = (offers: IOffer[]): IOffer | null => {
        if (!offers.length) return null;
        return offers.reduce((min, o) => (offerAmount(o) < offerAmount(min) ? o : min));
    };

    const allValidOffers: IOffer[] = [];

    if (trainLegIds.length > 0) {
        // Tag-narrowed candidates first, then fall back to the full premium
        // pool. This keeps the upgrade option visible when the backend's
        // UPGRADE=<code> hint resolves to a tier that doesn't actually cover
        // every leg of the trip (sold-out / mis-tagged) but a different
        // premium tier does.
        let pick = cheapestSingleCovering(candidates, trainLegIds);
        let bundle: IOffer[] | null = null;
        if (!pick && coversAllLegs(candidates, trainLegIds) && candidates.length > 0) {
            bundle = candidates;
        }
        if (!pick && !bundle && candidates !== premiumOffers) {
            pick = cheapestSingleCovering(premiumOffers, trainLegIds);
            if (!pick && coversAllLegs(premiumOffers, trainLegIds) && premiumOffers.length > 0) {
                bundle = premiumOffers;
            }
        }
        if (pick) {
            allValidOffers.push(pick);
        } else if (bundle) {
            // No single offer covers all train legs but the bundle does —
            // keep all so the booking remains complete.
            allValidOffers.push(...bundle);
        }
    } else {
        let pick = cheapestOffer(candidates);
        if (!pick && candidates !== premiumOffers) {
            pick = cheapestOffer(premiumOffers);
        }
        if (pick) allValidOffers.push(pick);
    }

    if (shuttleLegIds.length > 0) {
        if (coversAllLegs(shuttleOffers, shuttleLegIds) && shuttleOffers.length > 0) {
            allValidOffers.push(...shuttleOffers);
        }
    } else if (shuttleOffers.length > 0) {
        allValidOffers.push(...shuttleOffers);
    }

    return {
        trip,
        premiumOffers: allValidOffers,
    };
}

export async function requestUpgradeOffersForBooking(booking: IBooking): Promise<void> {
    updateRecoilState("upgradeOutboundOffersAtom", null);
    updateRecoilState("upgradeInboundOffersAtom", null);
    const passengers: IPassenger[] = booking?.passengers || [];
    const originalBookedOffers: IBookedOffer[] = booking?.bookedOffers || [];
    const originalTrips: any[] = booking?.trips || [];

    await getAllOfferWithoutExchangeAction.setParams(originalBookedOffers);
    await getAllOfferWithoutExchangeAction.execute();
    const offersWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchangeAction?.result || [];

    await getAllTripWithoutExchangeAction.setParams({ bookedOffers: offersWithoutExchange, trips: originalTrips });
    await getAllTripWithoutExchangeAction.execute();
    const tripsWithoutExchange: any[] = getAllTripWithoutExchangeAction?.result || [];

    const { tripsForUse, offersForUse } = scopeUpgradeToPrebookedTrain(tripsWithoutExchange, offersWithoutExchange);

    // Upgrades target train journeys only. Offers covering shuttle-only trips
    // (standalone onward shuttles) put their fulfillmentIds in the payload and
    // the backend rejects the WHOLE request with BOOKING_MODIFICATION_NOT_ALLOWED,
    // hiding the banner. In-journey FMLM shuttles live on trips that also carry
    // a train leg, so they keep working.
    const trainTripIds = new Set(preferTrainTrips(tripsForUse).map((t: any) => t?.id));
    const offersOnTrainTrips = offersForUse.filter((offer: IBookedOffer) =>
        trainTripIds.has(offer?.tripCoverage?.coveredTripId)
    );

    const outboundOffersByDirection = getOffersByDirection(offersOnTrainTrips, tripsForUse, TripTypeEnum.OUT_BOUND);
    const inboundOffersByDirection = getOffersByDirection(offersOnTrainTrips, tripsForUse, TripTypeEnum.IN_BOUND);

    const isStandard = (cls?: string | null) => cls === "standardValue" || cls === "standardPro";
    
    const hasStandardOutboundOffer = outboundOffersByDirection.some((offer: IBookedOffer) => {
        const selectedClass = getTrainClassTypeBaseOnProduct(offer.products || []);
        const hasShuttleCode = (offer.products || []).some((product: any) => product?.code === SHUTTLE_PRODUCT_CODE);
        return isStandard(selectedClass) || hasShuttleCode;
    });

    const hasStandardInboundOffer = inboundOffersByDirection.some((offer: IBookedOffer) => {
        const selectedClass = getTrainClassTypeBaseOnProduct(offer.products || []);
        const hasShuttleCode = (offer.products || []).some((product: any) => product?.code === SHUTTLE_PRODUCT_CODE);
        return isStandard(selectedClass) || hasShuttleCode;
    });

    // Fire outbound + inbound upgrade-offer fetches in parallel. Previously
    // these went through a shared singleton action with sequential
    // `setParams() → await execute()` calls, which forced wall-clock latency
    // to be the sum of both round-trips. Calling the API directly under
    // Promise.all halves the perceived latency without changing call count
    // (one POST per direction is required — they have disjoint
    // fulfillmentIds and the backend treats them independently).
    const buildPayload = (offers: IBookedOffer[]) => ({
        fulfillmentIds: getFulfilledFulfillmentIds(offers),
        passengerRefs: getPassengerRefsFromOffers(offers, passengers),
        currency: "AED",
        corporateCodes: null,
        promotionCodes: null,
    });

    const shouldFetchOutbound = outboundOffersByDirection.length > 0 && hasStandardOutboundOffer;
    const shouldFetchInbound = inboundOffersByDirection.length > 0 && hasStandardInboundOffer;

    const outboundPromise = shouldFetchOutbound
        ? upgradeToPremium(buildPayload(outboundOffersByDirection), booking?.id, false).catch((error) => {
            console.error("requestUpgradeOffersForBooking: outbound fetch failed", error);
            return null;
        })
        : Promise.resolve(null);

    const inboundPromise = shouldFetchInbound
        ? upgradeToPremium(buildPayload(inboundOffersByDirection), booking?.id, false).catch((error) => {
            console.error("requestUpgradeOffersForBooking: inbound fetch failed", error);
            return null;
        })
        : Promise.resolve(null);

    const [outboundResp, inboundResp] = await Promise.all([outboundPromise, inboundPromise]);

    const applyOffersToAtom = (
        resp: { status?: string; answer?: unknown } | null,
        atomName: "upgradeOutboundOffersAtom" | "upgradeInboundOffersAtom",
        currentSourceCode?: string,
    ) => {
        if (!resp || resp.status !== "ok" || !resp.answer) return;
        const offers = resp.answer as IUpgradeExchangeOfferResponse;
        if (offers && Array.isArray(offers.exchangeOffers)) {
            const filteredOffers = (offers?.trips || [])
                .map((trip: any) => processOffers(trip as ITrip, offers.exchangeOffers, currentSourceCode))
                .filter((res: any) => res && res.premiumOffers && res.premiumOffers.length > 0)
                .flatMap((res: any) => res.premiumOffers);

            const updatedOffers: IUpgradeExchangeOfferResponse = {
                ...offers,
                exchangeOffers: filteredOffers,
            };
            updateRecoilState(atomName, updatedOffers);
        } else {
            updateRecoilState(atomName, offers);
        }
    };

    // Resolve each leg's current source code from the RAW booked offers/trips,
    // not the prebook-filtered `offersForUse`. The other two entry paths
    // (ProcessUpgradeOffersAction, UpgradePremiumTrip) resolve from raw offers,
    // so feeding the same raw input here keeps all three identical — otherwise
    // a mixed-status booking (some legs PREBOOKED, others FULFILLED) could
    // narrow to a different subset and anchor on a different leg's tier
    // depending on entry path. resolveLegSourceCode already skips exchanged-out
    // offers, so raw input is safe.
    const outboundSourceCode = resolveLegSourceCode(getOffersByDirection(originalBookedOffers, originalTrips, TripTypeEnum.OUT_BOUND));
    const inboundSourceCode = resolveLegSourceCode(getOffersByDirection(originalBookedOffers, originalTrips, TripTypeEnum.IN_BOUND));

    applyOffersToAtom(outboundResp, "upgradeOutboundOffersAtom", outboundSourceCode);
    applyOffersToAtom(inboundResp, "upgradeInboundOffersAtom", inboundSourceCode);
}


